import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Archive, Search, RefreshCw, TrendingUp, TrendingDown, DollarSign,
  Eye, MousePointer, Target, Loader2, BarChart3, Calendar, Users,
  ChevronRight, CheckCircle2, XCircle, AlertTriangle, ArrowUpRight
} from 'lucide-react';
import { campaignAnalyticsAPI } from '../api';

const CampaignArchivesPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  useEffect(() => {
    fetchArchives();
  }, []);

  const fetchArchives = async () => {
    try {
      setLoading(true);
      const res = await campaignAnalyticsAPI.getArchived({ limit: 100 });
      setArchives(res.data?.archives || []);
    } catch (error) {
      console.error('Failed to fetch archives:', error);
      toast.error('Failed to load archived campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (archive) => {
    setSelectedArchive(archive);
    setShowDetailDialog(true);
  };

  const handleUnarchive = async (archiveId, campaignId) => {
    try {
      await campaignAnalyticsAPI.unarchiveCampaign(campaignId);
      toast.success('Campaign unarchived successfully');
      fetchArchives();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to unarchive campaign');
    }
  };

  const handleAnalyze = async (campaignIds) => {
    try {
      setAnalyzing(true);
      const res = await campaignAnalyticsAPI.analyzeCampaigns({
        campaign_ids: campaignIds,
        include_comparison: true
      });
      setAnalysisResults(res.data);
    } catch (error) {
      toast.error('Failed to analyze campaigns');
    } finally {
      setAnalyzing(false);
    }
  };

  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case 'highly_successful': return 'bg-green-100 text-green-800 border-green-200';
      case 'successful': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'mixed': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'challenging': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredArchives = archives.filter(a => 
    a.campaign_data?.name?.toLowerCase().includes(search.toLowerCase()) ||
    a.campaign_data?.campaign_type?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="campaign-archives-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Archive className="h-6 w-6" />
            Campaign Archives
          </h1>
          <p className="text-muted-foreground">Review and analyze past campaign performance</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search archives..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button variant="outline" onClick={fetchArchives}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Archived</p>
                <p className="text-2xl font-bold">{archives.length}</p>
              </div>
              <Archive className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Spend</p>
                <p className="text-2xl font-bold">
                  ${archives.reduce((sum, a) => sum + (a.results_summary?.total_spend || 0), 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Conversions</p>
                <p className="text-2xl font-bold">
                  {archives.reduce((sum, a) => sum + (a.results_summary?.total_conversions || 0), 0).toLocaleString()}
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg ROI</p>
                <p className="text-2xl font-bold">
                  {archives.length > 0 
                    ? (archives.reduce((sum, a) => sum + (a.results_summary?.roi || 0), 0) / archives.length).toFixed(1)
                    : 0}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Archives List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredArchives.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Archive className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-semibold mb-2">No Archived Campaigns</h3>
            <p className="text-muted-foreground">
              Campaigns will appear here once they are archived from the campaigns list.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredArchives.map(archive => (
            <Card key={archive.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{archive.campaign_data?.name}</h3>
                      <Badge variant="outline" className="capitalize">
                        {archive.campaign_data?.campaign_type?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        Archived {format(new Date(archive.archived_at), 'MMM d, yyyy')}
                      </span>
                      {archive.campaign_data?.start_date && (
                        <span>
                          Ran: {format(new Date(archive.campaign_data.start_date), 'MMM d')} - {' '}
                          {archive.campaign_data.end_date && format(new Date(archive.campaign_data.end_date), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                    
                    {/* Results Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Impressions</p>
                        <p className="font-bold">{(archive.results_summary?.total_impressions || 0).toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Clicks</p>
                        <p className="font-bold">{(archive.results_summary?.total_clicks || 0).toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Conversions</p>
                        <p className="font-bold">{(archive.results_summary?.total_conversions || 0).toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <p className="text-xs text-green-700">Spend</p>
                        <p className="font-bold text-green-700">${(archive.results_summary?.total_spend || 0).toLocaleString()}</p>
                      </div>
                      <div className={`p-3 rounded-lg ${archive.results_summary?.roi >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className={`text-xs ${archive.results_summary?.roi >= 0 ? 'text-green-700' : 'text-red-700'}`}>ROI</p>
                        <p className={`font-bold flex items-center gap-1 ${archive.results_summary?.roi >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {archive.results_summary?.roi >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                          {(archive.results_summary?.roi || 0).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2 ml-4">
                    <Button onClick={() => handleViewDetail(archive)}>
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </Button>
                    <Button variant="outline" onClick={() => handleAnalyze([archive.campaign_id])}>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Analyze
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedArchive?.campaign_data?.name}</DialogTitle>
            <DialogDescription>
              Archived on {selectedArchive?.archived_at && format(new Date(selectedArchive.archived_at), 'MMMM d, yyyy')}
            </DialogDescription>
          </DialogHeader>

          {selectedArchive && (
            <Tabs defaultValue="summary" className="mt-4">
              <TabsList>
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="results">Results History</TabsTrigger>
                <TabsTrigger value="workflow">Workflow Log</TabsTrigger>
                <TabsTrigger value="legal">Legal Reviews</TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="space-y-6 mt-4">
                {/* Campaign Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Campaign Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span className="capitalize">{selectedArchive.campaign_data?.campaign_type?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget:</span>
                        <span>${(selectedArchive.campaign_data?.budget || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Expected ROI:</span>
                        <span>{selectedArchive.campaign_data?.expected_roi || 0}%</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-blue-900">Final Results</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-700">CTR:</span>
                        <span className="font-medium">{selectedArchive.results_summary?.ctr}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">Conversion Rate:</span>
                        <span className="font-medium">{selectedArchive.results_summary?.conversion_rate}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-blue-700">ROI:</span>
                        <span className={`font-bold ${selectedArchive.results_summary?.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedArchive.results_summary?.roi}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Grid */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Eye className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                      <p className="text-2xl font-bold">{(selectedArchive.results_summary?.total_impressions || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Impressions</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <MousePointer className="h-6 w-6 mx-auto mb-2 text-green-500" />
                      <p className="text-2xl font-bold">{(selectedArchive.results_summary?.total_clicks || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Clicks</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Target className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                      <p className="text-2xl font-bold">{(selectedArchive.results_summary?.total_conversions || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Conversions</p>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="results" className="mt-4">
                {selectedArchive.results_history?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedArchive.results_history.map((entry, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg text-sm">
                        <span>{entry.date}</span>
                        <div className="flex items-center gap-4">
                          <span>Imp: {entry.impressions?.toLocaleString()}</span>
                          <span>Clicks: {entry.clicks?.toLocaleString()}</span>
                          <span>Conv: {entry.conversions?.toLocaleString()}</span>
                          <span className="text-green-600">Spend: ${entry.spend?.toFixed(2)}</span>
                        </div>
                        <Badge variant="outline">{entry.source}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No results history recorded</p>
                )}
              </TabsContent>

              <TabsContent value="workflow" className="mt-4">
                {selectedArchive.workflow_history?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedArchive.workflow_history.map((entry, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium capitalize">{entry.action_type?.replace('_', ' ')}</span>
                          <span className="text-xs text-muted-foreground">
                            {entry.created_at && format(new Date(entry.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        {entry.performed_by_name && (
                          <p className="text-sm text-muted-foreground">By: {entry.performed_by_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No workflow history</p>
                )}
              </TabsContent>

              <TabsContent value="legal" className="mt-4">
                {selectedArchive.legal_reviews?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedArchive.legal_reviews.map((review, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${review.passed ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {review.passed ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
                            <span className="font-medium">{review.passed ? 'Passed' : 'Failed'}</span>
                          </div>
                          <Badge variant={review.passed ? 'default' : 'destructive'}>{review.overall_score?.toFixed(0)}%</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {review.created_at && format(new Date(review.created_at), 'MMM d, yyyy h:mm a')}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">No legal reviews</p>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-6">
            {hasRole(['admin', 'marketing_manager']) && selectedArchive && (
              <Button 
                variant="outline" 
                onClick={() => handleUnarchive(selectedArchive.id, selectedArchive.campaign_id)}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Unarchive Campaign
              </Button>
            )}
            <Button onClick={() => setShowDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Analysis Results Dialog */}
      {analysisResults && (
        <Dialog open={!!analysisResults} onOpenChange={() => setAnalysisResults(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Campaign Analysis</DialogTitle>
              <DialogDescription>
                Performance analysis and recommendations
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {analysisResults.analyses?.map((analysis, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{analysis.campaign_name}</CardTitle>
                      <Badge className={getVerdictColor(analysis.verdict)}>
                        {analysis.verdict_label}
                      </Badge>
                    </div>
                    <CardDescription>
                      Overall Score: <span className="font-bold text-foreground">{analysis.overall_score}/100</span>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Success Factors */}
                    {analysis.success_factors?.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
                          <CheckCircle2 className="h-4 w-4" />
                          Success Factors
                        </h4>
                        <ul className="space-y-1">
                          {analysis.success_factors.map((f, i) => (
                            <li key={i} className="text-sm text-green-700">
                              <strong>{f.factor}:</strong> {f.detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Challenge Factors */}
                    {analysis.challenge_factors?.length > 0 && (
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                          <AlertTriangle className="h-4 w-4" />
                          Challenges
                        </h4>
                        <ul className="space-y-1">
                          {analysis.challenge_factors.map((f, i) => (
                            <li key={i} className="text-sm text-amber-700">
                              <strong>{f.factor}:</strong> {f.detail}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {analysis.recommendations?.length > 0 && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Recommendations</h4>
                        <ul className="space-y-2">
                          {analysis.recommendations.map((r, i) => (
                            <li key={i} className="text-sm">
                              <Badge variant="outline" className="mr-2 capitalize">{r.priority}</Badge>
                              <strong>{r.area}:</strong> {r.recommendation}
                              <span className="text-blue-600 ml-2">({r.expected_impact})</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            <DialogFooter>
              <Button onClick={() => setAnalysisResults(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CampaignArchivesPage;
